import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import api, { API } from "@/lib/api";
import { tabClass } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, Sprout, Mail, HardHat, FileText, Download, Trash2, Ban, RotateCcw, Flag, CheckCircle2, XCircle, Plus, RefreshCw } from "lucide-react";
import { MarkdownEditor } from "@/components/MarkdownEditor";

const PLAN_OPTIONS = [
  ["Free", "Free"], ["Pro", "Pro"], ["Orchardist", "Orchardist"],
  ["CrewBasic", "Crew Basic"], ["CrewPro", "Crew Pro"], ["CrewEnterprise", "Crew Enterprise"],
];
const TABS = [
  { key: "users", label: "User Management", icon: Users },
  { key: "plants", label: "Plant & Crop", icon: Sprout },
  { key: "emails", label: "Email Log", icon: Mail },
  { key: "crews", label: "Crew Management", icon: HardHat },
  { key: "content", label: "Content", icon: FileText },
];
const statusColor = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  suspended: "bg-red-100 text-red-700 border-red-200",
  delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

// ---------------------------------------------------------------- Users
const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkPlan, setBulkPlan] = useState("");
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const changePlan = async (u, plan) => {
    await api.post(`/admin/users/${u.id}/plan`, { plan });
    toast.success(`${u.name} → ${plan}`); load();
  };
  const suspend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/suspend`, { suspend: u.status !== "suspended" });
      toast.success(`${u.name} is now ${data.status}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This removes their plants too.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); setSelected((s) => { const n = new Set(s); n.delete(u.id); return n; }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendWelcome = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/welcome-email`);
      if (data.email_status === "failed") toast.warning(`Welcome email queued for ${u.email} (delivery pending — email service not configured in preview). Temp password logged in Email Log.`);
      else toast.success(`Welcome email sent to ${u.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send welcome email"); }
  };
  const download = async (url) => {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = href; a.download = "plantflow-users.csv"; a.click();
    URL.revokeObjectURL(href);
  };
  const exportAll = () => download("/admin/users/export");
  const exportSelected = () => download(`/admin/users/export?ids=${[...selected].join(",")}`);
  const applyBulkPlan = async (plan) => {
    if (!plan || selected.size === 0) return;
    const { data } = await api.post("/admin/users/bulk-plan", { ids: [...selected], plan });
    toast.success(`${data.updated} user(s) → ${plan}`); setBulkPlan(""); setSelected(new Set()); load();
  };

  const filtered = users.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filtered.forEach((u) => n.delete(u.id));
    else filtered.forEach((u) => n.add(u.id));
    return n;
  });

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <Input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name, email, role…" className="rounded-full max-w-md" />
        <Button data-testid="export-users-btn" onClick={exportAll} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-bar" className="mb-4 flex items-center gap-3 flex-wrap rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <span data-testid="bulk-count" className="text-sm font-medium text-emerald-900">{selected.size} selected</span>
          <select data-testid="bulk-plan-select" value={bulkPlan} onChange={(e) => { setBulkPlan(e.target.value); applyBulkPlan(e.target.value); }}
            className="rounded-lg border border-emerald-300 bg-white px-2 py-1 text-xs text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-300">
            <option value="" disabled>Change plan to…</option>
            {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Button data-testid="bulk-export-btn" size="sm" onClick={exportSelected} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white"><Download className="w-4 h-4 mr-1" /> Export selected</Button>
          <Button data-testid="bulk-clear-btn" size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="rounded-full text-stone-500 hover:text-stone-800 ml-auto">Clear</Button>
        </div>
      )}

      <p className="text-xs text-stone-400 mb-2" data-testid="user-count">{filtered.length} of {users.length} users</p>
      <div className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-emerald-50 text-emerald-800 text-left text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3"><input data-testid="select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-emerald-700 w-4 h-4 align-middle" /></th>
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Signup</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-t border-stone-100 hover:bg-stone-50/60 ${selected.has(u.id) ? "bg-emerald-50/50" : ""}`}>
                  <td className="px-4 py-2.5"><input data-testid={`user-select-${u.id}`} type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} className="accent-emerald-700 w-4 h-4 align-middle" /></td>
                  <td className="px-4 py-2.5 font-medium text-emerald-950">{u.name}</td>
                  <td className="px-4 py-2.5 text-stone-600">{u.email}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className="rounded-full capitalize">{u.role}</Badge></td>
                  <td className="px-4 py-2.5">
                    {u.role === "consumer" || u.role === "provider" || u.role === "admin" ? (
                      <select data-testid={`user-plan-select-${u.id}`} value={u.plan || ""} onChange={(e) => changePlan(u, e.target.value)}
                        className="rounded-lg border border-stone-200 bg-white px-2 py-1 text-xs text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-300">
                        <option value="" disabled>—</option>
                        {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    ) : <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-stone-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2.5"><Badge className={`rounded-full border capitalize ${statusColor[u.status]}`}>{u.status}</Badge></td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`user-welcome-${u.id}`} size="sm" variant="ghost" onClick={() => sendWelcome(u)} className="rounded-full text-stone-500 hover:text-emerald-700" title="Send Welcome Email"><Mail className="w-4 h-4" /></Button>
                      <Button data-testid={`user-suspend-${u.id}`} size="sm" variant="ghost" onClick={() => suspend(u)} className="rounded-full text-stone-500 hover:text-amber-700" title={u.status === "suspended" ? "Reactivate" : "Suspend"}>
                        {u.status === "suspended" ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                      <Button data-testid={`user-delete-${u.id}`} size="sm" variant="ghost" onClick={() => del(u)} className="rounded-full text-stone-500 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Plants
const PlantsPanel = () => {
  const [plants, setPlants] = useState([]);
  const load = useCallback(() => api.get("/admin/plants").then((r) => setPlants(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const flag = async (p) => { await api.post(`/admin/plants/${p.id}/flag`, { flag: !p.flagged }); load(); };

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {plants.length === 0 && <p className="text-sm text-stone-400">No plant profiles yet.</p>}
      {plants.map((p) => (
        <div key={p.id} data-testid={`plant-row-${p.id}`} className={`rounded-2xl border bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] ${p.flagged ? "border-red-300" : "border-black/5"}`}>
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl text-emerald-950">{p.name}</h3>
            <Button data-testid={`plant-flag-${p.id}`} size="sm" variant="ghost" onClick={() => flag(p)} className={`rounded-full ${p.flagged ? "text-red-600" : "text-stone-400 hover:text-red-500"}`} title="Flag for review"><Flag className="w-4 h-4" /></Button>
          </div>
          <p className="text-sm text-stone-500">{p.tree_type}</p>
          <p className="mt-1 text-xs text-stone-400">{p.owner_name} · {p.owner_email}</p>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-emerald-600" style={{ width: `${p.progress}%` }} /></div>
            <span className="text-xs font-medium text-emerald-800">{p.progress}%</span>
          </div>
          {p.flagged && <Badge className="mt-3 rounded-full border bg-red-100 text-red-700 border-red-200">Flagged for review</Badge>}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------- Emails
const EmailsPanel = () => {
  const [emails, setEmails] = useState([]);
  const load = useCallback(() => api.get("/admin/emails").then((r) => setEmails(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const resend = async (e) => {
    const { data } = await api.post(`/admin/emails/${e.id}/resend`);
    toast[data.status === "delivered" ? "success" : "error"](`Resend: ${data.status}`); load();
  };
  return (
    <div className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-emerald-50 text-emerald-800 text-left text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Recipient</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Sender</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
          </thead>
          <tbody>
            {emails.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">No emails dispatched yet.</td></tr>}
            {emails.map((e) => (
              <tr key={e.id} data-testid={`email-row-${e.id}`} className="border-t border-stone-100 hover:bg-stone-50/60">
                <td className="px-4 py-2.5 text-stone-600">{e.to}</td>
                <td className="px-4 py-2.5 text-emerald-950 max-w-xs truncate">{e.subject}</td>
                <td className="px-4 py-2.5 text-stone-500">{e.from_name}</td>
                <td className="px-4 py-2.5 text-stone-500 text-xs">{e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5"><Badge className={`rounded-full border capitalize ${statusColor[e.status]}`}>{e.status}</Badge></td>
                <td className="px-4 py-2.5 text-right">
                  {e.status === "failed" && <Button data-testid={`email-resend-${e.id}`} size="sm" variant="ghost" onClick={() => resend(e)} className="rounded-full text-emerald-700 hover:text-emerald-900"><RefreshCw className="w-4 h-4 mr-1" /> Resend</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Crews
const CrewsPanel = () => {
  const [crews, setCrews] = useState([]);
  const [providers, setProviders] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/crews").then((r) => setCrews(r.data));
    api.get("/admin/providers").then((r) => setProviders(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const assign = async (clientId, providerId) => {
    if (!providerId) return;
    await api.post(`/admin/clients/${clientId}/assign`, { provider_id: providerId });
    toast.success("Client reassigned"); load();
  };
  return (
    <div className="space-y-5">
      {crews.length === 0 && <p className="text-sm text-stone-400">No crew accounts yet.</p>}
      {crews.map((c) => (
        <div key={c.id} data-testid={`crew-${c.id}`} className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-serif text-2xl text-emerald-950">{c.company || c.name}</h3>
              <p className="text-sm text-stone-500">{c.name} · {c.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-200">{c.plan_label}</Badge>
              <Badge variant="outline" className="rounded-full">{c.tickets.open} open / {c.tickets.total} tickets</Badge>
            </div>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Crew members ({c.members.length})</p>
              <div className="space-y-1">
                {c.members.length === 0 && <p className="text-xs text-stone-400">No members.</p>}
                {c.members.map((m) => <p key={m.id} className="text-sm text-stone-600">{m.name} · <span className="text-stone-400">{m.email}</span></p>)}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Assigned clients ({c.clients.length})</p>
              <div className="space-y-2 max-h-52 overflow-auto">
                {c.clients.length === 0 && <p className="text-xs text-stone-400">No clients.</p>}
                {c.clients.map((cl) => (
                  <div key={cl.id} className="flex items-center justify-between gap-2 rounded-lg border border-stone-100 bg-stone-50/60 px-3 py-1.5">
                    <span className="text-sm text-emerald-950 truncate">{cl.name}</span>
                    <select data-testid={`assign-client-${cl.id}`} defaultValue="" onChange={(e) => assign(cl.id, e.target.value)}
                      className="rounded-lg border border-stone-200 bg-white px-2 py-1 text-xs text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-300">
                      <option value="" disabled>Reassign to…</option>
                      {providers.filter((p) => p.id !== c.id).map((p) => <option key={p.id} value={p.id}>{p.company || p.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------- Content
const ContentPanel = () => {
  const [drafts, setDrafts] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", category: "Announcement", cover: "", publishAt: "" });
  const [active, setActive] = useState(null);
  const load = useCallback(() => api.get("/admin/content").then((r) => setDrafts(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const onCover = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader(); reader.onload = () => setForm((f) => ({ ...f, cover: reader.result })); reader.readAsDataURL(file);
  };
  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body required");
    await api.post("/admin/content", { title: form.title, body: form.body, category: form.category, cover_image: form.cover || null, scheduled_at: form.publishAt || null });
    setForm({ title: "", body: "", category: "Announcement", cover: "", publishAt: "" }); toast.success("Draft created"); load();
  };
  const setStatus = async (d, status) => {
    const { data } = await api.post(`/admin/content/${d.id}/status`, { status });
    if (status === "approved") toast.success(data.blast ? "Approved — email blast sent to subscribers" : "Approved");
    load();
  };
  const del = async (d) => { if (!window.confirm("Delete this draft?")) return; await api.delete(`/admin/content/${d.id}`); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {active && <ContentModal draft={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />}
      <div className="lg:col-span-2 space-y-4">
        {drafts.length === 0 && <p className="text-sm text-stone-400">No content drafts.</p>}
        {drafts.map((d) => (
          <div key={d.id} data-testid={`content-${d.id}`} onClick={() => setActive(d)} className="rounded-2xl border border-black/5 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] cursor-pointer hover:border-emerald-300 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif text-xl text-emerald-950">{d.title}</h3>
                <Badge variant="outline" className="rounded-full text-emerald-700 border-emerald-200">{d.category}</Badge>
              </div>
              <Badge className={`rounded-full border capitalize ${statusColor[d.status]}`}>{d.status}</Badge>
            </div>
            <p className="mt-2 text-sm text-stone-600 line-clamp-2">{d.body}</p>
            <div className="mt-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Button data-testid={`content-approve-${d.id}`} size="sm" onClick={() => setStatus(d, "approved")} disabled={d.status === "approved"} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
              <Button data-testid={`content-reject-${d.id}`} size="sm" variant="outline" onClick={() => setStatus(d, "rejected")} disabled={d.status === "rejected"} className="rounded-full"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
              <Button data-testid={`content-delete-${d.id}`} size="sm" variant="ghost" onClick={() => del(d)} className="rounded-full text-stone-500 hover:text-red-600 ml-auto"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] h-fit">
        <h3 className="font-serif text-xl text-emerald-950 flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-700" /> New draft</h3>
        <Input data-testid="content-new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="mt-3 rounded-xl" />
        <select data-testid="content-new-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="mt-3 w-full rounded-xl border border-stone-200 bg-white px-3 h-10 text-sm text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-300">
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="block mt-3 text-xs uppercase tracking-wider text-stone-400">Body</label>
        <div className="mt-1"><MarkdownEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} testid="content-new-body" /></div>
        <label className="block mt-3 text-xs uppercase tracking-wider text-stone-400">Cover image</label>
        <input data-testid="content-new-cover" type="file" accept="image/*" onChange={onCover} className="mt-1 block w-full text-xs text-stone-500 file:mr-3 file:rounded-full file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-emerald-800 hover:file:bg-emerald-100" />
        {form.cover && <img src={form.cover} alt="cover preview" data-testid="content-cover-preview" className="mt-2 w-full h-28 object-cover rounded-xl" />}
        <label className="block mt-3 text-xs uppercase tracking-wider text-stone-400">Publish on (optional)</label>
        <Input data-testid="content-new-schedule" type="datetime-local" value={form.publishAt} onChange={(e) => setForm({ ...form, publishAt: e.target.value })} className="mt-1 rounded-xl" />
        <p className="mt-1 text-[11px] text-stone-400">Leave empty to publish &amp; email immediately on approval.</p>
        <Button data-testid="content-create" onClick={create} className="mt-3 w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Create draft</Button>
      </div>
    </div>
  );
};

const CATEGORY_OPTIONS = ["Announcement", "Newsletter", "Customer Story", "Tips", "Update"];

const ContentModal = ({ draft, onClose, onSaved }) => {
  const [f, setF] = useState({
    title: draft.title, body: draft.body, category: draft.category || "Update",
    blooming_tree: draft.blooming_tree || "", blooming_shrub: draft.blooming_shrub || "",
    seasonal_annuals: draft.seasonal_annuals || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => { await api.put(`/admin/content/${draft.id}`, f); };
  const act = async (status) => {
    setBusy(true);
    try {
      await save();
      const { data } = await api.post(`/admin/content/${draft.id}/status`, { status });
      toast.success(status === "approved" ? (data.blast ? "Approved — email blast sent to subscribers" : "Approved") : "Rejected");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const saveDraft = async () => {
    setBusy(true);
    try { await save(); toast.success("Draft saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div data-testid="content-modal" onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-2xl w-full my-8 p-7 shadow-2xl">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700">Edit content</span>
          <Badge className={`rounded-full border capitalize ${statusColor[draft.status]}`}>{draft.status}</Badge>
        </div>
        <label className="block mt-4 text-xs uppercase tracking-wider text-stone-400">Title</label>
        <Input data-testid="content-modal-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 rounded-xl" />
        <label className="block mt-4 text-xs uppercase tracking-wider text-stone-400">Category</label>
        <select data-testid="content-modal-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
          className="mt-1 w-full rounded-xl border border-stone-200 bg-white px-3 h-10 text-sm text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-300">
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="block mt-4 text-xs uppercase tracking-wider text-stone-400">Body</label>
        <div className="mt-1"><MarkdownEditor value={f.body} onChange={(v) => setF({ ...f, body: v })} testid="content-modal-body" /></div>
        <label className="block mt-4 text-xs uppercase tracking-wider text-stone-400">Blooming Trees</label>
        <Textarea data-testid="content-modal-blooming-tree" value={f.blooming_tree} onChange={(e) => setF({ ...f, blooming_tree: e.target.value })} placeholder="Enter blooming tree updates..." className="mt-1 rounded-xl min-h-[80px]" />
        <label className="block mt-4 text-xs uppercase tracking-wider text-stone-400">Blooming Shrubs</label>
        <Textarea data-testid="content-modal-blooming-shrub" value={f.blooming_shrub} onChange={(e) => setF({ ...f, blooming_shrub: e.target.value })} placeholder="Enter blooming shrub updates..." className="mt-1 rounded-xl min-h-[80px]" />
        <label className="block mt-4 text-xs uppercase tracking-wider text-stone-400">Seasonal Annuals</label>
        <Textarea data-testid="content-modal-seasonal-annuals" value={f.seasonal_annuals} onChange={(e) => setF({ ...f, seasonal_annuals: e.target.value })} placeholder="Enter seasonal annual updates..." className="mt-1 rounded-xl min-h-[80px]" />
        <p className="mt-2 text-xs text-stone-400">Approving publishes this to the public blog and emails all active subscribers.</p>
        <div className="mt-5 flex items-center gap-2">
          <Button data-testid="content-modal-approve" disabled={busy} onClick={() => act("approved")} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve &amp; publish</Button>
          <Button data-testid="content-modal-save" disabled={busy} variant="outline" onClick={saveDraft} className="rounded-full"><FileText className="w-4 h-4 mr-1" /> Save draft</Button>
          <Button data-testid="content-modal-reject" disabled={busy} variant="outline" onClick={() => act("rejected")} className="rounded-full"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
          <Button data-testid="content-modal-close" variant="ghost" onClick={onClose} className="rounded-full text-stone-500 hover:text-stone-800 ml-auto">Close</Button>
        </div>
      </div>
    </div>
  );
};

const StatsStrip = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  const cards = [
    { key: "total_users", label: "Total users", icon: Users },
    { key: "active_plants", label: "Active plants", icon: Sprout },
    { key: "emails_today", label: "Emails sent today", icon: Mail },
    { key: "open_jobs", label: "Open crew jobs", icon: HardHat },
  ];
  return (
    <div data-testid="admin-stats" className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-2xl border border-black/5 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-stone-400">{c.label}</span>
            <c.icon className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="mt-2 font-serif text-4xl text-emerald-950">{s ? s[c.key] : "—"}</p>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("users");
  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <span className="text-xs uppercase tracking-[0.3em] text-emerald-700">Platform administration</span>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-emerald-950">Admin Dashboard</h1>
        <p className="mt-2 text-stone-500 text-sm">Manage users, plants, email delivery, crews, and marketing content across PlantFlow.</p>

        <StatsStrip />

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`${tabClass(tab === t.key)} flex items-center gap-2`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-7">
          {tab === "users" && <UsersPanel />}
          {tab === "plants" && <PlantsPanel />}
          {tab === "emails" && <EmailsPanel />}
          {tab === "crews" && <CrewsPanel />}
          {tab === "content" && <ContentPanel />}
        </div>
      </div>
    </div>
  );
}
